import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import Cite from 'citation-js';
import {
  makeStyles,
  Dialog,
  DialogContent,
  DialogTitle as MuiDialogTitle,
  Typography,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';

import { Tab, Tabs, TabPanel } from '@redshelf/redshelf-ui';

import IconButton from 'components/IconButton';
import Modal from '../Modal';
import makeCitationObject from 'utilities/makeCitationObject';
import { APA, CHICAGO, MLA, SCREEN_SIZE_CATEGORIES } from 'constants';
import {
  UserContentItemModifiersContext,
  ViewportValuesContext,
} from 'contexts';
import CitationComponent from './CitationComponent';

const useStyles = makeStyles((theme) => {
  return {
    button: {
      margin: theme.spacing(1),
      padding: theme.spacing(1.5),
    },
    tabsContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    tabs: {
      margin: theme.spacing(3, 2, 2, 2),
      width: '100%',
      '& :not(.Mui-selected).MuiTab-secondary': {
        background: theme.palette.common.white,
      },
      '& .MuiTab-secondary:first-child': {
        borderRight: 'none',
      },
      '& .MuiTab-secondary:last-child': {
        borderLeft: 'none',
      },
      '& .MuiTab-secondary:not(:first-child):not(:last-child)': {
        borderWidth: '1px',
      },
      '& .MuiTab-selectedSecondary:hover': {
        color: '#FFFFFF',
        backgroundColor: theme.palette.shelfBlue['500'],
      },
    },
    tab: {
      width: '33.33%',
      fontWeight: 500,
      '&:focus-visible': {
        outline: `${theme.spacing(0.25)}px solid white`,
        outlineOffset: theme.spacing(-0.5),
      },
    },
    titleRoot: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      padding: theme.spacing(0.33333333, 2), // 2px 16px
      minHeight: theme.spacing(6.75), // 48px + 6px === Close icon size by default + the container offset
    },
    title: {
      marginRight: theme.spacing(6), // 48px === Close icon size by default
      fontSize: '1.175rem',
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0.33333333), // 2px
      right: theme.spacing(0.33333333),
      '&:hover': {
        background: theme.palette.shelfBlue['50L'],
      },
    },
    dialogContent: {
      padding: 0,
    },
    dialogPaper: {
      width: '90%',
      maxWidth: theme.spacing(37.5),
    },
    modal: {
      maxWidth: theme.spacing(37.5),
    },
  };
});

function initializeCitation(book) {
  Cite.CSL.register.addTemplate('apa', APA);
  Cite.CSL.register.addTemplate('chicago', CHICAGO);
  Cite.CSL.register.addTemplate('mla', MLA);
  const citationObject = makeCitationObject(book);

  let citation;
  try {
    citation = Cite(citationObject, { forceType: '@bibjson/record+object' });
  } catch (e) {
    console.warn('Citation creation failed', e);
    citation = Cite(
      { type: 'webpage', link: citationObject.link },
      { forceType: '@bibjson/record+object' },
    );
  }

  return citation;
}

function populateTextsArray(citation, type) {
  const citationStyles = { 0: 'apa', 1: 'chicago', 2: 'mla' };

  let textsArray = [];
  for (let citationStyleIndex in citationStyles) {
    const args = {
      format: 'text',
      template: citationStyles[citationStyleIndex],
      lang: 'en-US',
    };
    textsArray[citationStyleIndex] = citation.format(type, {
      ...args,
    });
  }

  return textsArray;
}

const CitationModal = ({
  anchorPosition,
  book,
  onClose = () => {},
  onCloseAll = () => {},
  shouldShowAsDialog, // Allow override for testing
  ...props
}) => {
  const classes = useStyles();
  const title = 'Copy a Citation';

  const { viewport } = useContext(ViewportValuesContext);
  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  shouldShowAsDialog =
    shouldShowAsDialog !== undefined // Allow override for testing
      ? shouldShowAsDialog
      : viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const tabProps = [{ label: 'APA' }, { label: 'Chicago' }, { label: 'MLA' }];

  const citation = initializeCitation(book);
  const citationTexts = populateTextsArray(citation, 'citation');
  const bibliographyTexts = populateTextsArray(citation, 'bibliography');

  const [currentCitationStyleIndex, setCurrentCitationStyleIndex] = useState(0);

  const handleClose = () => {
    onDialogClose('citationModal');
    onClose();
  };

  const handleCloseAll = () => {
    onDialogClose('citationModal');
    onCloseAll();
  };

  const DialogTitle = () => {
    return (
      <MuiDialogTitle className={classes.titleRoot} disableTypography>
        <Typography
          id="citation-dialog-title"
          className={classes.title}
          variant="h2"
        >
          {title}
        </Typography>
        {onClose && (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        )}
      </MuiDialogTitle>
    );
  };

  const citationTabs = (
    <div className={classes.tabsContainer}>
      <Tabs
        aria-label="citation format tabs"
        className={classes.tabs}
        disableTabPanelAttributes={Boolean(
          'disableTabPanelAttributes',
          false,
          'TabsProps',
        )}
        onChange={(_, newValue) => {
          setCurrentCitationStyleIndex(newValue);
        }}
        selectionFollowsFocus={Boolean(
          'selectionFollowsFocus',
          false,
          'TabsProps',
        )}
        tabVariant="secondary"
        value={currentCitationStyleIndex}
      >
        {tabProps.map(({ label }, index) => (
          <Tab
            className={classes.tab}
            label={label}
            key={label}
            aria-controls={`nav-tabpanel-${index}`}
          />
        ))}
      </Tabs>
    </div>
  );

  const citationContent = (
    <>
      {citationTabs}
      {citationTexts.map((text, index) => (
        <TabPanel
          disableSpacing={Boolean('disableSpacing', false, 'TabPanelProps')}
          index={index}
          key={text}
          value={currentCitationStyleIndex}
        >
          <CitationComponent
            bibliographyText={bibliographyTexts[index]}
            bibliographyTexts={bibliographyTexts}
            citationText={text}
            citationTexts={citationTexts}
            currentCitationStyleIndex={currentCitationStyleIndex}
            onClose={handleClose}
            onCloseAll={handleCloseAll}
          />
        </TabPanel>
      ))}
    </>
  );

  return (
    <div {...props}>
      {shouldShowAsDialog ? (
        <Dialog
          aria-labelledby="citation-dialog-title"
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') {
              handleClose();
            }
          }}
          open={true}
          PaperProps={{ className: classes.dialogPaper }}
          TransitionProps={{
            onEnter: () => onDialogOpen('citationModal'),
          }}
          disablePortal
        >
          <DialogTitle />
          <DialogContent className={classes.dialogContent} dividers>
            {citationContent}
          </DialogContent>
        </Dialog>
      ) : (
        <Modal
          anchorPosition={anchorPosition}
          onClose={handleClose}
          title={title}
          TransitionProps={{
            onEnter: () => onDialogOpen('citationModal'),
          }}
          className={classes.modal}
          disablePortal
        >
          {citationContent}
        </Modal>
      )}
    </div>
  );
};

CitationModal.propTypes = {
  anchorPosition: PropTypes.object.isRequired,
  book: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onCloseAll: PropTypes.func,
};

export default CitationModal;
